import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import get from "lodash/get";
import classnames from "classnames";
import queryString from "query-string";

import QUERY from "./queries";

import {
	PROMO_DESIGN,
	READ_RECEIPT_ANALYTICS_PROPS,
} from "analytics/read_receipts.js";
import {
	saveStateToUrl,
} from "apps/experiments/util/helpers";
import Analytics from "analytics/util/Analytics";
import { isDesktop } from "shared/util/EnvironmentHelpers";
import Context from "shared/util/Context";
import Str from "shared/components/Str";
import PrimaryButton from "shared/components/PrimaryButton";
import Modal from "shared/components/Modal";
import ProductPriceInfo from "./ProductPriceInfo";
import { getPlatform } from "shared/payment/services";
import { PRODUCT_TYPES, PRODUCT_SUBTYPES } from "shared/payment/constants";
import {
	getComparativeDiscountPercentageByInitialUnitPrice,
} from "shared/payment/helpers/getComparativeDiscount";
import CircularLoader from "shared/components/CircularLoader";
import { formatGraphQLProductInfoForLegacy } from "shared/payment/helpers/formatGraphQLProductInfoForLegacy";

import SmallBubbleIcon from "./svgs/small_bubble.svg";
import BigBubbleIcon from "./svgs/big_bubble.svg";
import DoubleCheck from "./svgs/read_receipts_big.svg";

import "./ReadReceiptRateCard.scss";

function directToMobilePayments(selectedProductPackage, correspondentId) {
	const oldState = queryString.parse(window.location.search);

	const qs = {
		productid: selectedProductPackage.productid,
		type: selectedProductPackage.productSubtype,
		correspondentId,
		...oldState,
	};

	window.location.href = `/payments?${queryString.stringify(qs)}`;
}

const ReadReceiptRateCard = ({
	isOpen,
	onClose,
	openPaymentModal,
	correspondentId,
	hasInlineCTA,
}) => {
	const {
		loading,
		data,
	} = useQuery(QUERY, {
		variables: {
			userId: Context.get("userid"),
			platform: getPlatform(),
			productType: PRODUCT_TYPES.TOKEN,
			productSubtype: PRODUCT_SUBTYPES.READ_RECEIPT,
		},
	});

	const products = get(data, "user.rateCard.productPriceInfoList") || [];

	const sortedProducts = useMemo(() =>
		products.sort((a, b) => b.tokensCount - a.tokensCount), [products]);

	const middleProduct = sortedProducts[1] || {};

	const [selectedProductId, setSelectedProductId] = useState(middleProduct.productId);

	useEffect(() => {
		if (sortedProducts[1]) {
			setSelectedProductId(sortedProducts[1].productId);
		}
	}, [sortedProducts]);

	const promoDesign = hasInlineCTA ? PROMO_DESIGN.IN_LINE : PROMO_DESIGN.HEADER;

	useEffect(() => {
		if (isOpen) {
			Analytics.logUIEvent(Analytics.eventNames.viewedRateCard, {
				data: {
					"promo design": promoDesign,
					...READ_RECEIPT_ANALYTICS_PROPS,
				},
			});
		}
	}, [isOpen, promoDesign]);

	let formattedDiscountPercentage;

	if (selectedProductId) {
		formattedDiscountPercentage =
			getComparativeDiscountPercentageByInitialUnitPrice(sortedProducts, selectedProductId);
	}

	const selectedProduct = useMemo(() => products.find((product) =>
		product.productId === selectedProductId), [products, selectedProductId]);

	return (
		<Modal
			className="read-receipt-rate-card-modal"
			show={isOpen}
			onCancel={onClose}
			noClose
		>
			<div className="read-receipt-rate-card">
				<div className="read-receipt-rate-card-header-container">
					<div className="read-receipt-rate-card-icons-container">
						<BigBubbleIcon className="read-receipt-rate-card-big-bubble" />
						<SmallBubbleIcon className="read-receipt-rate-card-small-bubble" />
					</div>
					<Str className="read-receipt-rate-card-header">
						Read Receipts
					</Str>
					<div className="read-receipt-rate-card-subheader">
						<div className="read-receipt-rate-card-subheader-text">
							<Str>
								Know if they‘ve read your messages with read receipts
							</Str>
						</div>
						<DoubleCheck className="read-receipt-rate-card-double-check" />
					</div>
				</div>
				<div className="read-receipt-rate-card-body">
					<div className={classnames("read-receipt-rate-card-product-info", {
						"read-receipt-rate-card-product-info-loading": loading,
					})}>
						{
							loading
								? <CircularLoader />
								: (
									sortedProducts.map((product) => {
										return (
											<ProductPriceInfo
												tokenCount={product.tokensCount}
												price={product.formattedInitialUnitPrice}
												key={product.productId}
												isSelected={selectedProductId === product.productId}
												setSelected={() => {
													setSelectedProductId(product.productId);
													saveStateToUrl({ promoDesign, source: "message thread" });
													Analytics.logUIEvent(Analytics.eventNames.selectedPackageOnRateCard, {
														data: {
															"promo design": promoDesign,
															...READ_RECEIPT_ANALYTICS_PROPS,
														},
													});
												}}
												discountPercentage={formattedDiscountPercentage}
											/>
										);
									})
								)
						}
					</div>
					<div className="read-receipt-rate-card-buttons">
						<PrimaryButton
							className="read-receipt-rate-card-submit-btn"
							onClick={() => {
								const legacyFormattedProduct =
									formatGraphQLProductInfoForLegacy(selectedProduct);

								if (isDesktop) {
									openPaymentModal(legacyFormattedProduct);
								} else {
									directToMobilePayments(legacyFormattedProduct, correspondentId);
								}

								onClose();
							}}
						>
							<Str>Continue</Str>
						</PrimaryButton>
						<button
							className="read-receipt-rate-card-cancel-btn"
							onClick={onClose}
						>
							<Str>No Thanks</Str>
						</button>
					</div>
				</div>
			</div>
		</Modal>
	);
};

ReadReceiptRateCard.defaultProps = {
	isOpen: false,
	openPaymentModal: () => {},
	hasInlineCTA: false,
};

ReadReceiptRateCard.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	openPaymentModal: PropTypes.func,
	correspondentId: PropTypes.string.isRequired,
	hasInlineCTA: PropTypes.bool,
};

export default ReadReceiptRateCard;



// WEBPACK FOOTER //
// ./src/shared/components/ReadReceiptRateCard/ReadReceiptRateCard.jsx